/**
 * WebMCP — Organization Tools (unified registration)
 *
 * Registers all organization CRUD tools at the OrganizationPage level so they
 * are available from ANY tab within /organization (Dashboard, Positions, etc.).
 *
 * When a tool is called and the user is on a different tab, it auto-navigates
 * to the correct tab before calling the API — no error, no manual step.
 *
 * Tools registered here:
 *   - create_employee_draft
 *   - create_position_draft
 *   - create_responsibility_draft
 *
 * Usage (OrganizationPage.vue — <script setup>):
 *   import { useOrganizationTools } from "@components/webmcp/tools/organization/useOrganizationTools.js";
 *   import { createEmployee, createPosition, createResponsibility } from "@/api";
 *
 *   const { proxy } = getCurrentInstance();
 *   useOrganizationTools({ createEmployee, createPosition, createResponsibility, proxy });
 */

import { onMounted, onBeforeUnmount, nextTick } from "vue";
import {
  registerTool,
  unregisterTool,
  waitForModelContext,
} from "../../core/useWebMcpRegistry.js";
import {
  registerDebugHook,
  unregisterDebugHook,
} from "../../core/useWebMcpDebug.js";
import {
  ORGANIZATION_ROUTE_NAME,
  ORGANIZATION_TABS,
} from "../../navigation/routeConstants.js";
import router from "@/router";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState.js";
import { useEmployeeStore } from "@/store/pinia/employee";
import { useWebMcpActionsStore } from "@/store/pinia/webmcpActions";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const resolveAccountId = () => {
  try {
    return JSON.parse(window.localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
};

/**
 * Navigates to targetTab if not already there.
 * Uses the router singleton so it works even after the component is unmounted.
 */
const navigateIfNeeded = async (targetTab) => {
  const currentTab = (router.currentRoute?.params?.tab || "").toLowerCase();
  if (currentTab !== targetTab.toLowerCase()) {
    await router
      .push({ name: ORGANIZATION_ROUTE_NAME, params: { tab: targetTab } })
      .catch(() => {});
  }
};

// ─── Tool: create_employee_draft ─────────────────────────────────────────────

const EMPLOYEE_TOOL = "create_employee_draft";

function buildEmployeeExecute({ createEmployee, proxy }) {
  return async (input = {}) => {
    const firstName = typeof input.firstName === "string"
      ? input.firstName.trim()
      : typeof input.first_name === "string"
        ? input.first_name.trim()
        : "";
    const lastName = typeof input.lastName === "string"
      ? input.lastName.trim()
      : typeof input.last_name === "string"
        ? input.last_name.trim()
        : "";

    if (!firstName || !lastName) {
      return { success: false, error: "Both firstName and lastName are required." };
    }

    const accountId = resolveAccountId();
    if (!accountId) {
      return { success: false, error: "Account ID is not available." };
    }

    await navigateIfNeeded(ORGANIZATION_TABS.EMPLOYEES);
    await nextTick();

    try {
      const viewState = useEmployeesOrganizationTableViewState();
      const employeeStore = useEmployeeStore();

      employeeStore.setCurrentEmployee(null);
      viewState.state.addEmployeeModal.show = true;
      await nextTick();

      const result = await viewState.handleEmployeeOperation(
        "edit_employee",
        accountId,
        { first_name: firstName, last_name: lastName, title: "", email: "" }
      );

      return {
        success: true,
        message: `Employee "${firstName} ${lastName}" draft created successfully.`,
        employee: {
          id: result?.createdEmployee?.id ?? null,
          first_name: firstName,
          last_name: lastName,
        },
      };
    } catch (error) {
      const apiError =
        error.response?.data?.email?.[0] ||
        error.response?.data?.first_name?.[0] ||
        error.response?.data?.detail;
      return {
        success: false,
        error: apiError || "Failed to create employee.",
      };
    }
  };
}

const employeeToolDefinition = (execute) => ({
  name: EMPLOYEE_TOOL,
  description:
    "Create a new employee draft in Organization > Employees. " +
    "Automatically navigates to the Employees tab if needed before creating.",
  inputSchema: {
    type: "object",
    properties: {
      firstName: { type: "string", description: "Employee first name." },
      lastName: { type: "string", description: "Employee last name." },
    },
    required: ["firstName", "lastName"],
    additionalProperties: false,
  },
  execute: async (input = {}) => execute(input),
  annotations: { readOnlyHint: false },
});

// ─── Tool: create_position_draft ─────────────────────────────────────────────

const POSITION_TOOL = "create_position_draft";

function buildPositionExecute({ createPosition, proxy }) {
  return async (input = {}) => {
    const name = typeof input.name === "string" ? input.name.trim() : "";
    if (!name) {
      return { success: false, error: "Position name is required." };
    }

    const accountId = resolveAccountId();
    if (!accountId) {
      return { success: false, error: "Account ID is not available." };
    }

    await navigateIfNeeded(ORGANIZATION_TABS.POSITIONS);

    try {
      const { data: newPosition } = await createPosition(accountId, {
        name,
        account: accountId,
      });

      if (!newPosition?.id) {
        return { success: false, error: "Position was created but no ID was returned." };
      }

      // Navigate to the new position detail page
      await router
        .push({
          name: "PositionDetailChild",
          params: { positionId: newPosition.id },
          query: { positionTab: "attached-roles" },
        })
        .catch(() => {});

      return {
        success: true,
        message: `Position "${name}" created. Navigating to its detail page.`,
        position: { id: newPosition.id, name: newPosition.name ?? name },
      };
    } catch (error) {
      const apiError =
        error.response?.data?.[0] ||
        error.response?.data?.name?.[0] ||
        error.response?.data?.detail;
      const isDuplicate =
        typeof apiError === "string" &&
        (apiError === "Same name already exists" || apiError.includes("already exists"));
      return {
        success: false,
        error: isDuplicate
          ? `A position named "${name}" already exists. Use a unique name.`
          : apiError || "Failed to create position.",
      };
    }
  };
}

const positionToolDefinition = (execute) => ({
  name: POSITION_TOOL,
  description:
    "Create a new Position draft in Organization > Positions. " +
    "Automatically navigates to the Positions tab if needed. " +
    "After creation, navigates to the position detail page (positionTab=attached-roles).",
  inputSchema: {
    type: "object",
    properties: {
      name: {
        type: "string",
        description: "Name of the position. Must be unique within the account.",
      },
    },
    required: ["name"],
    additionalProperties: false,
  },
  execute: async (input = {}) => execute(input),
  annotations: { readOnlyHint: false },
});

// ─── Tool: create_responsibility_draft ───────────────────────────────────────

const RESPONSIBILITY_TOOL = "create_responsibility_draft";

function buildResponsibilityExecute() {
  return async (input = {}) => {
    const name = typeof input.name === "string" ? input.name.trim() : "";
    if (!name) {
      return { success: false, error: "Responsibility name is required." };
    }

    await navigateIfNeeded(ORGANIZATION_TABS.RESPONSIBILITIES);
    await new Promise((r) => setTimeout(r, 400));

    return new Promise((resolve) => {
      const store = useWebMcpActionsStore();
      store.pendingCreateResponsibility = {
        name,
        resolve: (result) => {
          store.pendingCreateResponsibility = null;
          resolve(result);
        },
      };
    });
  };
}

const responsibilityToolDefinition = (execute) => ({
  name: RESPONSIBILITY_TOOL,
  description:
    "Create a new Responsibility draft in Organization > Responsibilities. " +
    "Automatically navigates to the Responsibilities tab if needed before creating.",
  inputSchema: {
    type: "object",
    properties: {
      name: {
        type: "string",
        description: "Name of the responsibility. Must be unique.",
      },
    },
    required: ["name"],
    additionalProperties: false,
  },
  execute: async (input = {}) => execute(input),
  annotations: { readOnlyHint: false },
});
// ─── Tool: create_role_draft ─────────────────────────────────────────────────

const ROLE_TOOL = "create_role_draft";

function buildRoleExecute() {
  return async (input = {}) => {
    const name = typeof input.name === "string" ? input.name.trim() : "";
    if (!name) {
      return { success: false, error: "Role name is required." };
    }

    await navigateIfNeeded(ORGANIZATION_TABS.ROLES);
    await new Promise((r) => setTimeout(r, 400));

    return new Promise((resolve) => {
      const store = useWebMcpActionsStore();
      store.pendingCreateRole = {
        name,
        resolve: (result) => {
          store.pendingCreateRole = null;
          resolve(result);
        },
      };
    });
  };
}

const roleToolDefinition = (execute) => ({
  name: ROLE_TOOL,
  description:
    "Create a new Role draft in Organization > Roles. " +
    "Automatically navigates to the Roles tab if needed. " +
    "After creation, navigates to the role detail page (roleTab=attached_responsibilities).",
  inputSchema: {
    type: "object",
    properties: {
      name: {
        type: "string",
        description: "Name of the role. Must be unique within the account.",
      },
    },
    required: ["name"],
    additionalProperties: false,
  },
  execute: async (input = {}) => execute(input),
  annotations: { readOnlyHint: false },
});
// ─── Composable ──────────────────────────────────────────────────────────────

/**
 * @param {object}   options
 * @param {function} options.createEmployee       - from @/api
 * @param {function} options.createPosition       - from @/api
 * @param {object}   options.proxy                - Vue component proxy
 */
export function useOrganizationTools({
  createEmployee,
  createPosition,
  proxy,
}) {
  let cancelWait = null;
  const debugHandlers = {};

  // Build execute functions once
  const executeEmployee = buildEmployeeExecute({ createEmployee, proxy });
  const executePosition = buildPositionExecute({ createPosition, proxy });
  const executeResponsibility = buildResponsibilityExecute();
  const executeRole = buildRoleExecute();

  const tools = [
    { def: employeeToolDefinition(executeEmployee), execute: executeEmployee, name: EMPLOYEE_TOOL },
    { def: positionToolDefinition(executePosition), execute: executePosition, name: POSITION_TOOL },
    { def: responsibilityToolDefinition(executeResponsibility), execute: executeResponsibility, name: RESPONSIBILITY_TOOL },
    { def: roleToolDefinition(executeRole), execute: executeRole, name: ROLE_TOOL },
  ];

  onMounted(() => {
    cancelWait = waitForModelContext(() => {
      tools.forEach(({ def }) => {
        registerTool(def, { onWarning: (msg, err) => console.warn(msg, err) });
      });
    });

    tools.forEach(({ name, execute }) => {
      const handler = async (input) => execute(input);
      debugHandlers[name] = handler;
      registerDebugHook(name, handler);
    });
  });

  onBeforeUnmount(() => {
    cancelWait?.();
    tools.forEach(({ name }) => {
      unregisterTool(name);
      unregisterDebugHook(name, debugHandlers[name]);
    });
  });
}
